import { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MFChannel } from '../../../Components/SDK';
import SearchBox from '../../components/SearchBox';
import { setCategoryChannels } from '../../../slice/explorechannel';
import { ExploreChannelItem } from '../../components';
import logger from '../../../Helpers/logger';

function CategoryBasedChannels() {
    const dispatch = useDispatch();
    const { activeCategory } = useSelector(state => state.exploreChannelsSlice);

    const { categoryChannels } = useSelector(state => state.exploreChannelsSlice);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const containerRef = useRef(null);

    const [searchText, setSearchText] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const debounceMs = 300;
    const lastFetchedSearchRef = useRef(null);

    const updateSearchText = useCallback(newSearch => {
        setSearchText(newSearch);
    }, []);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchText), debounceMs);
        return () => clearTimeout(t);
    }, [searchText, debounceMs]);

    useEffect(() => {
        setSearchText('');
        setDebouncedSearch('');
        lastFetchedSearchRef.current = null;
    }, [activeCategory.id]);

    const fetchPage = useCallback(
        async (page = 1, search = '') => {
            if (!activeCategory.id) return null;
            try {
                if (page === 1) setLoading(true);
                else setLoadingMore(true);

                const res = await MFChannel.fetchChannelListBasedonCategoryId({
                    search,
                    categoryId: activeCategory.id,
                    size: 20,
                    page,
                });
                const payload = res?.data ?? res ?? {};
                return payload;
            } catch (err) {
                logger.error('fetch category channels', err);
                return null;
            } finally {
                if (page === 1) setLoading(false);
                else setLoadingMore(false);
            }
        },
        [activeCategory.id],
    );

    // handle load more when scrolled to bottom
    const handleScroll = useCallback(
        async e => {
            const el = e.target;
            const threshold = 80;
            if (!el) return;
            const { scrollTop, scrollHeight, clientHeight } = el;
            if (scrollTop + clientHeight >= scrollHeight - threshold) {
                const currentPage = categoryChannels?.currentPage ?? 0;
                const totalPages = categoryChannels?.totalPages ?? 0;
                const nextPage = currentPage + 1;
                if (loadingMore) return;
                if (nextPage <= totalPages) {
                    const payload = await fetchPage(nextPage, searchText);
                    if (payload && payload.channels && Array.isArray(payload.channels)) {
                        // merge existing and new channels and dispatch merged payload
                        const existing = categoryChannels?.channels ?? [];
                        const merged = [...existing, ...payload.channels];
                        const mergedPayload = {
                            channels: merged,
                            currentPage: payload.currentPage ?? nextPage,
                            size: payload.size ?? merged.length,
                            totalPages: payload.totalPages ?? payload.total_pages ?? totalPages,
                            totalRecords:
                                payload.totalRecords ??
                                payload.totalRecords ??
                                categoryChannels?.totalRecords ??
                                merged.length,
                        };
                        dispatch(setCategoryChannels(mergedPayload));
                    }
                }
            }
        },
        [categoryChannels, fetchPage, dispatch, loadingMore, searchText],
    );

    useEffect(() => {
        const run = async () => {
            if (!activeCategory.id) return;
            if (debouncedSearch === lastFetchedSearchRef.current) return; // avoid duplicate fetch
            lastFetchedSearchRef.current = debouncedSearch;

            const payload = await fetchPage(1, debouncedSearch);
            if (payload) dispatch(setCategoryChannels(payload));
            else
                dispatch(
                    setCategoryChannels({ channels: [], currentPage: 0, size: 0, totalPages: 0, totalRecords: 0 }),
                );
        };
        run();
    }, [debouncedSearch, activeCategory.id, dispatch, fetchPage]);

    useEffect(() => {
        const node = containerRef.current;
        if (!node) return;
        node.addEventListener('scroll', handleScroll, { passive: true });
        return () => node.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    return (
        <>
            <SearchBox onChange={updateSearchText} autoFocus value={searchText} />
            <div className="channels-list-container category-channels-page">
                {loading && <div style={{ padding: 12 }}>Loading...</div>}
                <div className="explore-category-header">
                    <h3>{activeCategory.category}</h3>
                </div>
                <div ref={containerRef} className="category-channels-list">
                    {!loading && (
                        <>
                            {categoryChannels?.channels && categoryChannels.channels.length ? (
                                categoryChannels.channels.map(ch => (
                                    <ExploreChannelItem key={ch.channelId || ch.id} item={ch} />
                                ))
                            ) : (
                                <div className="no_channeltitle">No channels found for this category.</div>
                            )}

                            {loadingMore && <div style={{ textAlign: 'center', padding: 12 }}>Loading more...</div>}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default CategoryBasedChannels;
